/**
 * Генерация идентификаторов.
 *
 * ID создаются на клиенте, а не на сервере — иначе офлайн-создание записи было
 * бы невозможно. Используется UUID v4: вероятность коллизии между устройствами
 * пренебрежимо мала, а значит, локально созданная запись безопасно доедет до
 * сервера со своим исходным идентификатором.
 */

export function createId(): string {
  // `randomUUID` доступен в защищённом контексте (https / localhost) и в
  // WebView Capacitor. Fallback нужен для http-доступа в локальной сети.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return fallbackUuid();
}

function fallbackUuid(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }

  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // версия 4
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // вариант RFC 4122

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * Короткий человекочитаемый код — для инвайтов и шаринга списков.
 * Алфавит без похожих символов (0/O, 1/I/l), чтобы код можно было продиктовать.
 */
export function createShortCode(length = 6): string {
  const alphabet = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}
